import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { FuelService } from 'src/app/services/fuel/fuel.service';
import { FuelModalService } from 'src/app/services/fuel-modal/fuel-modal.service';
import Swal from 'sweetalert2';

declare var bootstrap: any;

@Component({
  selector: 'app-add-edit-fuel',
  templateUrl: './add-edit-fuel.component.html',
  styleUrls: ['./add-edit-fuel.component.scss']
})
export class AddEditFuelComponent extends DestructorComponent implements OnInit {

  @Output() fuelAdded = new EventEmitter<void>();
  @Output() fuelUpdated = new EventEmitter<void>();

  fuelForm!: FormGroup;
  isLoading = false;
  isEditMode = false;
  selectedFuel: any = null;

  constructor(
    private fb: FormBuilder,
    private _fuelService: FuelService,
    private _fuelModalService: FuelModalService
  ) {
    super();
    this.initializeForm();
  }

  ngOnInit(): void {
    this.setupModalListener();
  }

  private initializeForm(): void {
    this.fuelForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(2)]]
    });
  }

  // Getter methods for form controls
  get name() { return this.fuelForm.get('name'); }

  private setupModalListener(): void {
    const modalElement = document.getElementById('kt_modal_fuel_add_edit');
    if (modalElement) {
      modalElement.addEventListener('shown.bs.modal', () => {
        const serviceFuel = this._fuelModalService.getSelectedFuel();
        if (serviceFuel) {
          this.isEditMode = true;
          this.selectedFuel = serviceFuel;
          this.populateForm(this.selectedFuel);
        } else {
          this.isEditMode = false;
          this.selectedFuel = null;
          this.resetForm();
        }
      });

      modalElement.addEventListener('hidden.bs.modal', () => {
        this._fuelModalService.clearSelectedFuel();
        this.resetForm();
      });
    }
  }

  private populateForm(fuel: any): void {
    this.fuelForm.patchValue({
      name: fuel.name || ''
    });
  }

  private resetForm(): void {
    this.fuelForm.reset();
    this.isLoading = false;
    this.markFormGroupUntouched(this.fuelForm);
  }

  private markFormGroupUntouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsUntouched();
      control?.markAsPristine();
    });
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();
    });
  }

  private closeModal(): void {
    const modalElement = document.getElementById('kt_modal_fuel_add_edit');
    if (modalElement) {
      const modal = bootstrap.Modal.getInstance(modalElement);
      if (modal) {
        modal.hide();
      }
    }
  }

  onSubmit(): void {
    if (this.fuelForm.valid && !this.isLoading) {
      this.isLoading = true;

      const formData = this.fuelForm.value;

      // Remove empty fields
      const submitData: any = {};
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null && formData[key] !== '') {
          submitData[key] = formData[key];
        }
      });

      const apiCall = this.isEditMode
        ? this._fuelService.UpdateData(this.selectedFuel._id, submitData)
        : this._fuelService.AddData(submitData);

      this.subscriptions.push(
        apiCall.subscribe({
          next: (response: any) => {
            this.isLoading = false;
            console.log('Fuel operation successful:', response);

            // Show success message
            Swal.fire({
              text: this.isEditMode
                ? "Fuel has been successfully updated!"
                : "Fuel has been successfully created!",
              icon: "success",
              buttonsStyling: false,
              confirmButtonText: "Ok, got it!",
              customClass: {
                confirmButton: "btn btn-primary"
              }
            }).then((result: any) => {
              if (result.isConfirmed) {
                // Close the modal
                this.closeModal();

                // Emit appropriate event
                if (this.isEditMode) {
                  this.fuelUpdated.emit();
                } else {
                  this.fuelAdded.emit();
                }

                // Reset the form
                this.resetForm();
              }
            });
          },
          error: (error: any) => {
            this.isLoading = false;
            console.error('Fuel operation failed:', error);

            // Show error message
            Swal.fire({
              text: error.error?.message || "An error occurred. Please try again.",
              icon: "error",
              buttonsStyling: false,
              confirmButtonText: "Ok, got it!",
              customClass: {
                confirmButton: "btn btn-primary"
              }
            });
          }
        })
      );
    } else {
      this.markFormGroupTouched(this.fuelForm);
    }
  }
}
